import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

const KIND_LABEL = {
  profit_for_period: 'Profit / (loss)',
  contributions: 'Contribution',
  distributions: 'Distribution',
  opening_balance_adjustments: 'Opening balance',
  reserve_movements: 'Reserve',
  other_movements: 'Other',
};

function Amount({ value, fmt, bold }) {
  const v = Number(value) || 0;
  const cls = v < 0 ? 'text-red-700' : 'text-slate-900';
  return (
    <span className={`tabular-nums ${cls} ${bold ? 'font-semibold' : ''}`}>
      {v === 0 ? '—' : v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v)}
    </span>
  );
}

export const StatementOfChangesInEquity = ({ data, formatAmount }) => {
  const [showAccounts, setShowAccounts] = useState(false);
  const [showMovements, setShowMovements] = useState(false);
  if (!data?.rows) return null;
  const components = data.components || [];
  const recon = data.reconciliation || {};
  const fmt = (v) => formatAmount(v);
  const isTotalRow = (r) => r.key === 'opening' || r.key === 'closing';
  const movementRows = data.rows.filter((r) => !isTotalRow(r) && (r.total !== 0 || Object.values(r.values).some((v) => v !== 0)));

  return (
    <div className="space-y-6" data-testid="soce-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ['Opening equity', recon.opening_equity, 'soce-opening'],
          ['Movements in period', recon.total_movements, 'soce-movements'],
          ['Closing equity', recon.closing_equity, 'soce-closing'],
          ['Balance sheet equity', recon.balance_sheet_equity, 'soce-bs-equity'],
        ].map(([label, value, tid]) => (
          <Card key={tid} className="bg-[#283F8B]/5">
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
              <p className="text-xl mt-1" data-testid={tid}><Amount value={value} fmt={fmt} bold /></p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Statement of Changes in Equity · {data.period?.start} → {data.period?.end}</span>
            <Badge className={recon.ties ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} data-testid="soce-ties-badge">
              {recon.ties ? 'Reconciled to balance sheet' : `Does not tie (${fmt(recon.difference)})`}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="soce-table">
            <thead>
              <tr className="border-b-2 border-slate-300 text-left">
                <th className="py-2 pr-4 font-medium text-slate-600 min-w-[240px]"></th>
                {components.map((c) => (
                  <th key={c.key} className="py-2 px-3 text-right font-medium text-slate-600 whitespace-nowrap">{c.label}</th>
                ))}
                <th className="py-2 pl-3 text-right font-semibold text-slate-800">Total equity</th>
              </tr>
            </thead>
            <tbody>
              {[data.rows[0], ...movementRows, data.rows[data.rows.length - 1]].map((r) => (
                <tr key={r.key} className={isTotalRow(r) ? 'bg-slate-50 border-t-2 border-slate-300' : 'border-b border-slate-100'} data-testid={`soce-row-${r.key}`}>
                  <td className={`py-2 pr-4 ${isTotalRow(r) ? 'font-semibold' : 'pl-4'}`}>{r.label}</td>
                  {components.map((c) => (
                    <td key={c.key} className="py-2 px-3 text-right"><Amount value={r.values[c.key]} fmt={fmt} bold={isTotalRow(r)} /></td>
                  ))}
                  <td className="py-2 pl-3 text-right"><Amount value={r.total} fmt={fmt} bold /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-3">
            Retained earnings are derived from the profit & loss ledgers (prior periods + this period), exactly as on the balance sheet.
            Closing retained earnings <Amount value={recon.retained_earnings_check?.soce_closing} fmt={fmt} /> vs balance sheet <Amount value={recon.retained_earnings_check?.balance_sheet} fmt={fmt} />.
          </p>
        </CardContent>
      </Card>

      {data.accounts?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <Button variant="ghost" size="sm" className="justify-start px-0" onClick={() => setShowAccounts((v) => !v)} data-testid="soce-toggle-accounts">
              {showAccounts ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              Equity accounts ({data.accounts.length})
            </Button>
          </CardHeader>
          {showAccounts && (
            <CardContent>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-slate-600"><th className="py-1">Account</th><th className="py-1">Column</th><th className="py-1 text-right">Opening</th><th className="py-1 text-right">Movement</th><th className="py-1 text-right">Closing</th></tr></thead>
                <tbody>
                  {data.accounts.map((a) => (
                    <tr key={a.code} className="border-b border-slate-100" data-testid={`soce-account-${a.code}`}>
                      <td className="py-1 font-mono text-xs">{a.code} <span className="font-sans text-sm">{a.name}</span></td>
                      <td className="py-1 text-xs text-slate-500">{components.find((c) => c.key === a.component)?.label || a.component}</td>
                      <td className="py-1 text-right"><Amount value={a.opening} fmt={fmt} /></td>
                      <td className="py-1 text-right"><Amount value={a.movement} fmt={fmt} /></td>
                      <td className="py-1 text-right"><Amount value={a.closing} fmt={fmt} bold /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
        </Card>
      )}

      {data.movements?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <Button variant="ghost" size="sm" className="justify-start px-0" onClick={() => setShowMovements((v) => !v)} data-testid="soce-toggle-movements">
              {showMovements ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              Equity movements in period ({data.movements.length})
            </Button>
          </CardHeader>
          {showMovements && (
            <CardContent>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-slate-600"><th className="py-1">Date</th><th className="py-1">Reference</th><th className="py-1">Description</th><th className="py-1">Account</th><th className="py-1">Type</th><th className="py-1 text-right">Amount</th></tr></thead>
                <tbody>
                  {data.movements.map((m, i) => (
                    <tr key={`${m.journal_id}-${i}`} className="border-b border-slate-100">
                      <td className="py-1 whitespace-nowrap">{m.date}</td>
                      <td className="py-1 font-mono text-xs">{m.reference}</td>
                      <td className="py-1 max-w-[320px] truncate" title={m.description}>{m.description}</td>
                      <td className="py-1 text-xs">{m.account_code} {m.account_name}</td>
                      <td className="py-1 text-xs text-slate-500">{KIND_LABEL[m.kind] || m.kind}</td>
                      <td className="py-1 text-right"><Amount value={m.amount} fmt={fmt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default StatementOfChangesInEquity;
